// Protection Analytics Report — layout store.
//
// Holds the user's dashboard arrangement (metric tiles + chart panels: order,
// visibility, and per-slot content config) plus the deepdive table's dimensions
// and columns. Persisted to localStorage via Zustand `persist`.
//
// SNAPSHOT / DRAFT MODEL (the customize flow's reset semantics):
//   • `committed` is the saved layout — the ONLY thing persisted.
//   • the top-level layout fields are the WORKING DRAFT the report renders from;
//     they equal `committed` except while you're editing.
//   • edits mutate the draft (live preview). Save commits draft → committed.
//     Discard reverts draft → committed. Restore-defaults sets draft = factory
//     (you still have to Save to keep it).
// So "Reset/Discard" returns to your LAST SAVE, not to factory — and nothing is
// silently persisted until you Save.
//
// `editMode` is transient UI state, excluded from persistence.

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { toast } from 'react-toastify'
import { AnalyticsViews } from 'services/user'
import {
	DEFAULT_VISIBLE_COL_KEYS,
	ALL_METRIC_COL_KEYS,
	TableConfig
} from '../types'
import { DEFAULT_PRIMARY_DIMENSION } from '../data/dimension-registry'

// Per-slot content config. A chart panel renders straight off these; a metric
// tile off metric + dimension. Order/visibility live in the arrays below.
export interface ChartCfg {
	metric: string
	dimension: string
	/** Secondary "stack by" dimension — only used by the Stacked bar type (its two
	 *  segments are this dimension's split); ignored by Bar / Pie. */
	dimension2: string
	chartType: string
	stack100: boolean
}
export interface MetricCfg {
	metric: string
	dimension: string
}

// Metrics that only read as a Bar chart (ticket §4.6 compatibility matrix).
// "Bad traffic rate" is a percentage and "Blocked sources" is a unique count, so
// a donut or stacked bar (both share-of-total shapes) is nonsensical for either —
// the UI offers Bar only, switching a slot to one of these snaps its chart type
// back to Bar (see setChartCfg), and a persisted config that still pairs one with
// another type is coerced on load (see clone). Single source of truth for the
// on-card menu + drawer.
export const BAR_ONLY_METRICS = new Set(['Bad traffic rate', 'Blocked sources'])

// The chart type a slot may actually render: Bar for a bar-only metric, else
// whatever was asked for.
export const compatibleChartType = (metric: string, chartType: string): string =>
	BAR_ONLY_METRICS.has(metric) ? 'Bar' : chartType

// The stack-by dimension of a Stacked bar must differ from its primary
// dimension (DEV-2432 §5 / §5.5: "Country by Country" is meaningless and the
// engine rejects it). The pickers hide the other slot's value, and this is the
// fallback for a persisted config that still clashes: Status, unless Status IS
// the primary, then IP usage.
export const resolveStackBy = (dimension: string, dimension2: string | undefined): string => {
	if (dimension2 && dimension2 !== dimension) return dimension2
	return dimension === 'Status' ? 'IP usage' : 'Status'
}

// Editable section headings, keyed by the customize drawer's two modes. Shared
// by the drawer's "Section name" field and the live dashboard section headers so
// a rename in either place reflects in the other (and persists / reverts).
export interface SectionNames {
	metric: string
	chart: string
	dimension: string
}

// The full saveable layout — everything Save commits and Reset/Discard reverts.
export interface ReportLayout {
	metricOrder: string[]
	chartOrder: string[]
	hiddenMetrics: string[]
	hiddenCharts: string[]
	chartConfig: Record<string, ChartCfg>
	metricConfig: Record<string, MetricCfg>
	sectionNames: SectionNames
	/** Deepdive table settings (dimensions + column choice/order), persisted with
	 *  the view so the user's table arrangement survives reload / view switch. */
	tableConfig: TableConfig
}

/** Factory table settings: primary dimension only (flat list), default columns
 *  in their natural order. */
export const DEFAULT_TABLE_CONFIG: TableConfig = {
	dim1: DEFAULT_PRIMARY_DIMENSION,
	dim2: null,
	dim3: null,
	visibleColumns: [...DEFAULT_VISIBLE_COL_KEYS],
	columnOrder: [...ALL_METRIC_COL_KEYS]
}

// Author recorded on a view saved before the session user is known (the store is
// bound to the signed-in user by `configureViews` as soon as the user store
// resolves). Never a real name: an unknown author must not pass as anyone's, so
// `isViewOwner` is false for it and the backend copy (which carries the real
// owner id) wins on the next sync.
const UNKNOWN_AUTHOR = '__unknown__'

// A named, reusable report layout. "yours" = private to the user; "shared" =
// visible to the workspace. Each view carries its OWN full layout snapshot, so
// switching views swaps the whole dashboard arrangement. This is what "Save as
// new / shared view" creates and the title's template menu loads.
//
// `author` is who created the view (its display name). It drives the author line
// shown under shared views AND the permission model: only the author may rename,
// delete, or change the scope of a shared view — everyone else can still see it,
// duplicate it, or set it as their own default.
/** A report's live schedule as the Share drawer's form record. Its PRESENCE is
 *  what makes a report "scheduled" — that single fact drives the drawer's footer
 *  (Schedule vs Stop/Save). Read from the backend per view (data/use-view-schedule),
 *  never persisted here: a teammate's schedule counts too. */
export interface ScheduleCfg {
	/** Cadence label, e.g. 'Weekly' (matches the drawer's interval options). */
	interval: string
	/** ISO yyyy-mm-dd the schedule starts from — the next run steps off this. */
	startDate: string
	/** Reporting window label, e.g. 'Last 30 days'. */
	range: string
	/** Title recipients see on the delivered report. */
	name: string
	adAccounts: string[]
	campaigns: string[]
	/** Email addresses the report is sent to. */
	recipients: string[]
	/** Backend scheduled-report id once persisted — lets edit/stop target the row
	 *  (PATCH/DELETE) instead of only mutating local state. Null until created. */
	reportId?: string | null
}

/** Where a white-label report's header logo comes from: the workspace's own
 *  image, the website's image, or a custom upload made in this drawer. */
export type WhiteLabelLogoSource = 'workspace' | 'website' | 'custom'

/** Website-level saved branding. Draft uploads may temporarily use a data URI. */
export interface WhiteLabelBranding {
	/** Whether reports carry the brand instead of ClickGuard. */
	enabled: boolean
	/** Which image feeds the header. `workspace`/`website` pull the live account
	 *  or asset avatar; `custom` uses `customLogo`. */
	logoSource: WhiteLabelLogoSource
	/** Saved upload URL, or a data URI while editing. */
	customLogo: string | null
	/** Header/accent color applied to white-label output. */
	brandColor: string
}

/** Seed brand color — the DS primary/500 purple the drawer's picker also defaults
 *  to, so an untouched profile reads as "not customized". */
export const DEFAULT_BRAND_COLOR = '#554bfd'

/** 'default' = ClickGuard's read-only views (§2.2), everyone sees them; 'yours' =
 *  the user's private views; 'shared' = visible to the workspace. */
export type ViewScope = 'default' | 'yours' | 'shared'
export interface SavedView {
	id: string
	label: string
	scope: ViewScope
	/** Owner user id (ownership checks compare this to the signed-in user). */
	author: string
	/** Owner display name for "Created by {name}" (§2.6); absent → falls back to the id. */
	authorName?: string
	layout: ReportLayout
	/** Default views: position in the Default views menu. */
	sortOrder?: number
}

/** True when the current user created the view (so they may manage it). Compares
 *  against the signed-in user id; nobody owns anything until that is configured. */
export const isViewOwner = (v: SavedView): boolean => {
	const uid = useReportLayoutStore.getState().currentUserId
	return uid !== null && v.author === uid
}

// Factory defaults for the chart panels (ids match charts-row PANEL_IDS). The
// seed reproduces the report's out-of-the-box look (ip = stacked bar, veracity =
// pie, time = bar).
export const DEFAULT_CHART_CONFIG: Record<string, ChartCfg> = {
	ip: {
		metric: 'Ad clicks analyzed',
		dimension: 'IP usage',
		dimension2: 'Status',
		chartType: 'Bar',
		// Only a Stacked bar reads the stretch toggle; a Bar plots metric values.
		stack100: false
	},
	veracity: {
		metric: 'Bad clicks',
		dimension: 'Status',
		// A stack-by can never equal the primary, so the Status donut's latent
		// stack-by is IP usage (the value it would take when switched to a bar).
		dimension2: 'IP usage',
		chartType: 'Pie',
		stack100: false
	},
	time: {
		// High-cardinality dimension (Keyword has 14+ values) so the "top 3 + Other"
		// bucketing is visible out of the box for review — the summary chart keeps the
		// three biggest keywords and rolls the long tail into a hover-able "Other".
		metric: 'Bad clicks',
		dimension: 'Keyword',
		dimension2: 'Status',
		chartType: 'Bar',
		stack100: false
	}
}

// Factory defaults for the metric tiles. These ids MUST mirror the default
// (first-five) tile labels in `components/key-metrics-data.tsx` `METRICS` — the
// drawer + tiles seed `metricOrder` from those labels and resolve each slot's
// config here, so a mismatch leaves the customize drawer's Metrics tab blank.
// Kept as a local literal (not an import) so the store stays free of component
// modules — importing key-metrics-data.tsx, which builds JSX icons at load,
// created a require cycle that blanked the app.
const KEY_METRIC_IDS = [
	'Prevented ad waste',
	'Blocked sources',
	'Bad traffic rate',
	'Bad ad clicks',
	'Suspicious ad clicks'
]
export const DEFAULT_METRIC_CONFIG: Record<string, MetricCfg> =
	Object.fromEntries(
		KEY_METRIC_IDS.map((id) => [id, { metric: id, dimension: 'Campaign type' }])
	)

// Factory section headings (match the dashboard's out-of-the-box titles).
export const DEFAULT_SECTION_NAMES: SectionNames = {
	metric: 'Key metrics',
	chart: 'Trends & breakdowns',
	dimension: 'Dimensional analysis'
}

// The factory layout — the target of "Restore defaults".
export const DEFAULT_LAYOUT: ReportLayout = {
	metricOrder: [],
	chartOrder: [],
	hiddenMetrics: [],
	hiddenCharts: [],
	chartConfig: DEFAULT_CHART_CONFIG,
	metricConfig: DEFAULT_METRIC_CONFIG,
	sectionNames: DEFAULT_SECTION_NAMES,
	tableConfig: DEFAULT_TABLE_CONFIG
}

interface ReportLayoutState extends ReportLayout {
	/** Transient — not persisted. */
	editMode: boolean

	// The last SAVED layout. Persisted; the draft above is reset to it on Discard.
	committed: ReportLayout

	// ── Deepdive table dirty coordination ──────────────────────────────────────
	// The table keeps its dimensions/columns in local component state, so it can't
	// be structurally compared here. Instead it reports whether its saveable
	// settings diverge from its own baseline via `tableDirty`, and listens to the
	// commit/revert nonces (bumped by Save/Discard) to re-baseline or roll back.
	// All transient (never persisted).
	tableDirty: boolean
	setTableDirty: (b: boolean) => void
	tableCommitNonce: number
	tableRevertNonce: number

	// ── Saved views ───────────────────────────────────────────────────────────
	// Named layouts the user can switch between. `activeViewId` is the view the
	// report is currently showing (null = the unsaved default report).
	savedViews: SavedView[]
	activeViewId: string | null


	/** Bumped each time a different report/view is loaded — the page watches it to
	 *  softly fade the dashboard on a report switch (so the change isn't abrupt). */
	reportSwapNonce: number
	/** A view the user picked that the page is transitioning TO: set on select, the
	 *  page dissolves the current report out, then calls `applyView` to swap the data
	 *  at the invisible point and dissolve the new one in. Null when idle. Transient. */
	pendingSwapId: string | null
	/** Request a view switch WITH the out→in transition (vs `applyView`, which swaps
	 *  immediately). The page owns the choreography; this just names the target. */
	requestViewSwap: (id: string) => void
	/** The view auto-loaded when none is active (null = factory report). */
	defaultViewId: string | null
	/** Snapshot the current draft as a brand-new view, commit it, make it active. Returns the new id. */
	saveAsView: (name: string, scope: ViewScope) => string
	/** Register a brand-new view seeded from the default report. Unlike saveAsView it
	 *  does NOT touch the visible layout — pair with requestViewSwap so the reset to
	 *  default plays through the page's dissolve. Returns the new id. */
	createDefaultView: (name: string, scope: ViewScope) => string
	/** Load a saved view's layout into the report (draft + committed) and make it active. */
	applyView: (id: string) => void
	/** Rename a saved view (mirrors an inline title rename of the active view). */
	renameView: (id: string, label: string) => void
	/** Duplicate a view as a new private ("yours") copy, loaded + made active. Returns the new id. */
	duplicateView: (id: string) => string
	/** Delete a view; if it was active or the default, fall back to the factory report. */
	deleteView: (id: string) => void
	/** Mark a view as the default (or null to clear). */
	setDefaultViewId: (id: string | null) => void
	/** Move a view between "yours" (private) and "shared" (workspace-visible). */
	setViewScope: (id: string, scope: ViewScope) => void

	// ── Backend persistence (DEV-2377) ─────────────────────────────────────────
	/** The signed-in user id (view ownership) + website scope for the views API. */
	currentUserId: string | null
	viewsAccountId: string | null
	viewsAssetId: string | null
	/** Bind the current user + website so the view mutations can persist. */
	configureViews: (accountId: string, assetId: string, userId: string) => void
	/** Replace the saved views + the user's main-view pointer + the fallback default
	 *  (the first Default view, §2.10) from the backend (load on mount). */
	hydrateViews: (views: SavedView[], defaultViewId: string | null, fallbackViewId: string | null) => void
	/** The Default view that opens when the user has no main view (or it's gone). */
	fallbackViewId: string | null
	/** True once `savedViews` reflects the backend for the current user + website.
	 *  Before that the list is whatever localStorage held — possibly another
	 *  user's or workspace's — so nothing should act on it (deep links wait). */
	viewsHydrated: boolean
	/** Load the report that opens by default for this user on this website (§2.10):
	 *  their main view when it still exists, else the fallback default report. Runs
	 *  after every hydrate — on first visit AND on every workspace / website switch —
	 *  so the last-viewed view never carries over from another context or session. */
	loadInitialView: (id: string | null) => void
	/** Swap an optimistic local view id for the backend-assigned one on create. */
	reconcileViewId: (tempId: string, realId: string) => void

	// ── Customize session ─────────────────────────────────────────────────────
	/** Commit the working draft to the saved snapshot (and persist). */
	saveLayout: () => void
	/** Throw away edits since the last save: draft ← committed. */
	discardLayout: () => void

	// ── Draft mutations (live preview; committed on Save) ──────────────────────
	initMetricOrder: (ids: string[]) => void
	initChartOrder: (ids: string[]) => void
	reorderMetric: (from: number, to: number) => void
	reorderChart: (from: number, to: number) => void
	toggleMetricHidden: (id: string) => void
	toggleChartHidden: (id: string) => void
	setChartCfg: (id: string, patch: Partial<ChartCfg>) => void
	setMetricCfg: (id: string, patch: Partial<MetricCfg>) => void
	/** Rename a section heading (shared by the drawer field + dashboard header). */
	setSectionName: (key: keyof SectionNames, name: string) => void
	/** Per-card Reset: revert just this slot to the last saved snapshot. */
	resetChartSlot: (id: string) => void
	resetMetricSlot: (id: string) => void

	// ── Deepdive table ────────────────────────────────────────────────────────
	/** Push the grid's current settings into the working draft so they're captured
	 *  by Save (into the view's config) and drive structural dirty detection. The
	 *  grid owns the interactive state and mirrors it here on change. */
	setTableConfig: (cfg: TableConfig) => void
}

// Deep-clone a layout AND normalise it: every field falls back to the factory
// default when absent. A view's `config` comes from the backend as an opaque
// blob (an older/partial one may be missing chartConfig/sectionNames/etc.), and
// this is the single choke point all view layouts pass through (applyView,
// saveAsView, merge…), so normalising here keeps a bad config from crashing the
// report instead of blanking it.
const clone = (l: ReportLayout): ReportLayout => ({
	metricOrder: [...(l.metricOrder ?? [])],
	chartOrder: [...(l.chartOrder ?? [])],
	hiddenMetrics: [...(l.hiddenMetrics ?? [])],
	hiddenCharts: [...(l.hiddenCharts ?? [])],
	// A view saved before a metric became Bar-only (or a hand-edited default) may
	// still pair it with a donut / stacked bar; coerce to Bar so the panel and
	// its pickers agree with the §4.6 matrix instead of drawing a share-of-total
	// shape for a rate or a unique count.
	chartConfig: Object.fromEntries(
		Object.entries(l.chartConfig ?? DEFAULT_CHART_CONFIG).map(([k, v]) => [
			k,
			{ ...v, chartType: compatibleChartType(v.metric, v.chartType) }
		])
	),
	metricConfig: Object.fromEntries(
		Object.entries(l.metricConfig ?? DEFAULT_METRIC_CONFIG).map(([k, v]) => [k, { ...v }])
	),
	sectionNames: { ...DEFAULT_SECTION_NAMES, ...(l.sectionNames ?? {}) },
	// Views saved before table-config persistence (DEV-2377 follow-up) have no
	// tableConfig — fall back to the factory settings so they still open cleanly.
	tableConfig: l.tableConfig
		? {
				...l.tableConfig,
				visibleColumns: [...l.tableConfig.visibleColumns],
				columnOrder: [...l.tableConfig.columnOrder]
		  }
		: {
				...DEFAULT_TABLE_CONFIG,
				visibleColumns: [...DEFAULT_TABLE_CONFIG.visibleColumns],
				columnOrder: [...DEFAULT_TABLE_CONFIG.columnOrder]
		  }
})

// Snapshot the current working draft (the layout fields on the state) into a
// standalone ReportLayout — used by Save / Save-as to capture what's on screen.
const pickLayout = (s: ReportLayout): ReportLayout =>
	clone({
		metricOrder: s.metricOrder,
		chartOrder: s.chartOrder,
		hiddenMetrics: s.hiddenMetrics,
		hiddenCharts: s.hiddenCharts,
		chartConfig: s.chartConfig,
		metricConfig: s.metricConfig,
		sectionNames: s.sectionNames,
		tableConfig: s.tableConfig
	})

// ── Dirty detection (unsaved edits) ─────────────────────────────────────────
// The customize flow is always on now, so the Save/Discard buttons key off
// whether the working draft has diverged from the last save — not off an edit
// toggle. This is a cheap structural compare of draft vs committed.
const arrEq = (a: readonly string[], b: readonly string[]): boolean =>
	a.length === b.length && a.every((x, i) => x === b[i])
const cfgMapEq = (a: Record<string, any>, b: Record<string, any>): boolean => {
	const ak = Object.keys(a)
	if (ak.length !== Object.keys(b).length) return false
	return ak.every((k) => {
		const av = a[k]
		const bv = b[k]
		if (!bv) return false
		const fk = Object.keys(av)
		return (
			fk.length === Object.keys(bv).length && fk.every((f) => av[f] === bv[f])
		)
	})
}
export const sameLayout = (a: ReportLayout, b: ReportLayout): boolean =>
	arrEq(a.metricOrder, b.metricOrder) &&
	arrEq(a.chartOrder, b.chartOrder) &&
	arrEq(a.hiddenMetrics, b.hiddenMetrics) &&
	arrEq(a.hiddenCharts, b.hiddenCharts) &&
	cfgMapEq(a.chartConfig, b.chartConfig) &&
	cfgMapEq(a.metricConfig, b.metricConfig) &&
	a.sectionNames.metric === b.sectionNames.metric &&
	a.sectionNames.chart === b.sectionNames.chart &&
	a.sectionNames.dimension === b.sectionNames.dimension &&
	sameTableConfig(a.tableConfig, b.tableConfig)

// The deepdive table's persisted settings. `visibleColumns` is always serialized
// in columnOrder order (see the grid), so an ordered compare is valid here.
const sameTableConfig = (a: TableConfig, b: TableConfig): boolean =>
	a.dim1 === b.dim1 &&
	a.dim2 === b.dim2 &&
	a.dim3 === b.dim3 &&
	arrEq(a.visibleColumns, b.visibleColumns) &&
	arrEq(a.columnOrder, b.columnOrder)

/** True when the working draft diverges from the last save — either the layout
 *  (compared here) OR the deepdive table, whose own local state reports its
 *  divergence via `tableDirty`. Drives the Save/Discard vs Share swap. */
export const selectDirty = (s: ReportLayoutState): boolean =>
	!sameLayout(s, s.committed) || s.tableDirty

// Seeded views so the title menu's "Your views" / "Shared views" are populated
// and selectable out of the box (each starts from the factory layout). Real
// saves append to this list and persist.
//
// Authors are seeded to model ownership: "Your views" are private, so always
// yours; "Shared views" mix some you created (full control) with some teammates
// created (you can see / duplicate / set-as-default, but NOT rename, delete, or
// re-scope). The user's real views load from the backend via useViewsSync →
// hydrateViews on mount; the store starts empty so no placeholder/demo views
// ever flash before that resolves.
const SEED_VIEWS: SavedView[] = []

const arrayMove = <T>(arr: T[], from: number, to: number): T[] => {
	if (
		from === to ||
		from < 0 ||
		to < 0 ||
		from >= arr.length ||
		to >= arr.length
	)
		return arr
	const next = arr.slice()
	const [moved] = next.splice(from, 1)
	next.splice(to, 0, moved)
	return next
}

// Reconcile a persisted order with the section's current id set: keep known ids
// in their saved order, append any new ids, drop ids that no longer exist.
const reconcileOrder = (saved: string[], ids: string[]): string[] => {
	const kept = saved.filter((id) => ids.includes(id))
	const added = ids.filter((id) => !kept.includes(id))
	return [...kept, ...added]
}

const toggle = (list: string[], id: string) =>
	list.includes(id) ? list.filter((x) => x !== id) : [...list, id]

export const useReportLayoutStore = create<ReportLayoutState>()(
	persist(
		(set, get) => ({
			// Customize is always on now — there's no "enter edit" step. The
			// Save/Discard vs Share buttons key off `selectDirty`, not this flag.
			editMode: true,

			// Backend persistence context (set on page mount via configureViews).
			currentUserId: null,
			viewsAccountId: null,
			viewsAssetId: null,
			configureViews: (accountId, assetId, userId) =>
				set({ viewsAccountId: accountId, viewsAssetId: assetId, currentUserId: userId }),
			fallbackViewId: null,
			viewsHydrated: false,
			hydrateViews: (views, defaultViewId, fallbackViewId) =>
				set({ savedViews: views, defaultViewId, fallbackViewId, viewsHydrated: true }),
			loadInitialView: (id) =>
				set((s) => {
					// No main view (or it no longer exists) → the fallback Default view
					// (§2.10); the factory layout only when no defaults are seeded at all.
					const wanted = id ?? s.fallbackViewId
					const view = wanted ? s.savedViews.find((v) => v.id === wanted) : undefined
					const layout = clone(view ? view.layout : DEFAULT_LAYOUT)
					return {
						...layout,
						committed: clone(layout),
						activeViewId: view ? view.id : null,
						pendingSwapId: null,
						// If a dissolve-out was in flight, clearing pendingSwapId cancels its
						// applyView — bump the nonce so the page still dissolves back IN
						// instead of sitting blurred at 12%.
						...(s.pendingSwapId ? { reportSwapNonce: s.reportSwapNonce + 1 } : {})
					}
				}),
			// Swap the optimistic id for the backend's. Every reference follows — the
			// active view AND a swap still pending on the temp id (a new view is
			// created, then dissolved to via requestViewSwap; if the create resolves
			// before the 160ms dissolve-out, applyView must look up the REAL id).
			reconcileViewId: (tempId, realId) =>
				set((s) => ({
					savedViews: s.savedViews.map((v) =>
						v.id === tempId ? { ...v, id: realId } : v
					),
					activeViewId: s.activeViewId === tempId ? realId : s.activeViewId,
					pendingSwapId: s.pendingSwapId === tempId ? realId : s.pendingSwapId
				})),
			...clone(DEFAULT_LAYOUT),
			committed: clone(DEFAULT_LAYOUT),

			tableDirty: false,
			setTableDirty: (b) => set({ tableDirty: b }),
			tableCommitNonce: 0,
			tableRevertNonce: 0,

			savedViews: SEED_VIEWS.map((v) => ({ ...v, layout: clone(v.layout) })),
			activeViewId: null,
			reportSwapNonce: 0,
			pendingSwapId: null,
			requestViewSwap: (id) => set({ pendingSwapId: id }),
			defaultViewId: null,

			saveLayout: () => {
				set((s) => {
					const committed = pickLayout(s)
					// If a saved view is active, "Save changes" writes back into it so
					// the view itself reflects the edits (not just the live report).
					const savedViews = s.activeViewId
						? s.savedViews.map((v) =>
								v.id === s.activeViewId ? { ...v, layout: clone(committed) } : v
						  )
						: s.savedViews
					// Edit mode stays on (always-on customize); Save just clears dirty.
					// Bump the commit nonce so the table re-baselines its local state.
					return {
						committed,
						savedViews,
						editMode: true,
						tableDirty: false,
						tableCommitNonce: s.tableCommitNonce + 1
					}
				})
				// Persist the active view's new config to the backend. The API rejects
				// default views (immutable) — that path is a no-op here (caught).
				const { activeViewId, viewsAccountId, viewsAssetId, committed } = get()
				if (activeViewId && viewsAccountId && viewsAssetId) {
					AnalyticsViews.update(viewsAccountId, viewsAssetId, activeViewId, {
						config: committed as unknown as Record<string, unknown>
					}).catch(() => {
						// Surface the failure — otherwise the edit looks saved but silently
						// diverges from the server on reload.
						toast.error('Could not save your changes to this view. Please try again.')
					})
				}
			},
			discardLayout: () =>
				set((s) => ({
					...clone(s.committed),
					editMode: true,
					// Bump the revert nonce so the table rolls its local state back.
					tableDirty: false,
					tableRevertNonce: s.tableRevertNonce + 1
				})),

			saveAsView: (name, scope) => {
				const id = `v_${Date.now()}`
				const { currentUserId, viewsAccountId, viewsAssetId } = get()
				set((s) => {
					const committed = pickLayout(s)
					const view: SavedView = {
						id,
						label: name,
						scope,
						author: currentUserId ?? UNKNOWN_AUTHOR,
						layout: clone(committed)
					}
					return {
						committed,
						savedViews: [...s.savedViews, view],
						activeViewId: id,
						editMode: true
					}
				})
				// Persist to the backend, then swap the optimistic id for the real one.
				if (viewsAccountId && viewsAssetId) {
					AnalyticsViews.create(viewsAccountId, viewsAssetId, {
						name,
						scope: scope === 'shared' ? 'shared' : 'private',
						config: get().committed as unknown as Record<string, unknown>
					})
						.then((dto) => get().reconcileViewId(id, dto.id))
						.catch(() => {
							toast.error('Could not save the new view. Please try again.')
						})
				}
				return id
			},
			createDefaultView: (name, scope) => {
				const id = `v_${Date.now()}`
				const { currentUserId, viewsAccountId, viewsAssetId } = get()
				set((s) => ({
					savedViews: [
						...s.savedViews,
						{
							id,
							label: name,
							scope,
							// Owned by the signed-in user so they can manage it — not the
							// seed display name.
							author: currentUserId ?? UNKNOWN_AUTHOR,
							layout: clone(DEFAULT_LAYOUT)
						}
					]
				}))
				// Persist like saveAsView, then swap the optimistic id for the real one,
				// so a freshly-added view survives reload and is server-owned.
				if (viewsAccountId && viewsAssetId) {
					AnalyticsViews.create(viewsAccountId, viewsAssetId, {
						name,
						scope: scope === 'shared' ? 'shared' : 'private',
						config: clone(DEFAULT_LAYOUT) as unknown as Record<string, unknown>
					})
						.then((dto) => get().reconcileViewId(id, dto.id))
						.catch(() => {
							toast.error('Could not save the new view. Please try again.')
						})
				}
				return id
			},
			applyView: (id) =>
				set((s) => {
					const view = s.savedViews.find((v) => v.id === id)
					// Unknown id (e.g. the view was removed or re-keyed between the swap
					// request and the dissolve-out finishing): still clear the pending
					// swap and bump the nonce so the page dissolves back IN — returning
					// nothing left the dashboard stuck blurred at 12% (Cherry #0001).
					if (!view) {
						return { reportSwapNonce: s.reportSwapNonce + 1, pendingSwapId: null }
					}
					const layout = clone(view.layout)
					return {
						...layout,
						committed: clone(layout),
						activeViewId: id,
						reportSwapNonce: s.reportSwapNonce + 1,
						pendingSwapId: null,
						editMode: true
					}
				}),
			renameView: (id, label) => {
				set((s) => {
					// Only the author may rename a view (UI gates this too).
					const v = s.savedViews.find((x) => x.id === id)
					if (!v || !isViewOwner(v)) return {}
					return {
						savedViews: s.savedViews.map((x) =>
							x.id === id ? { ...x, label } : x
						)
					}
				})
				const { viewsAccountId, viewsAssetId } = get()
				if (viewsAccountId && viewsAssetId) {
					AnalyticsViews.update(viewsAccountId, viewsAssetId, id, {
						name: label
					}).catch(() => undefined)
				}
			},
			duplicateView: (id) => {
				const newId = `v_${Date.now()}`
				const { currentUserId, viewsAccountId, viewsAssetId, savedViews } = get()
				const src = savedViews.find((v) => v.id === id)
				set((s) => {
					if (!src) return {}
					// A copy is always private to the user AND authored by them, regardless
					// of the source's scope/author — so you can duplicate a teammate's shared
					// view and then fully manage your own private copy.
					const copy: SavedView = {
						id: newId,
						label: `${src.label} copy`,
						scope: 'yours',
						author: currentUserId ?? UNKNOWN_AUTHOR,
						layout: clone(src.layout)
					}
					const layout = clone(copy.layout)
					return {
						...layout,
						committed: clone(layout),
						savedViews: [...s.savedViews, copy],
						activeViewId: newId,
						editMode: true
					}
				})
				// Persist as a fresh private view from the source's layout (works even if
				// the source is a local-only draft), then reconcile the optimistic id.
				if (src && viewsAccountId && viewsAssetId) {
					AnalyticsViews.create(viewsAccountId, viewsAssetId, {
						name: `${src.label} copy`,
						scope: 'private',
						config: src.layout as unknown as Record<string, unknown>
					})
						.then((dto) => get().reconcileViewId(newId, dto.id))
						.catch(() => undefined)
				}
				return newId
			},
			deleteView: (id) => {
				set((s) => {
					// Only the author may delete a view (UI gates this too).
					const target = s.savedViews.find((v) => v.id === id)
					if (!target || !isViewOwner(target)) return {}
					const savedViews = s.savedViews.filter((v) => v.id !== id)
					const defaultViewId = s.defaultViewId === id ? null : s.defaultViewId
					// (The backend delete cascades the view's scheduled report; the drawer
					// reads schedules from the backend, so there's nothing local to drop.)
					// Deleting the active view drops the report onto the fallback Default
					// view (§2.10), or the factory layout when none is seeded.
					if (s.activeViewId === id) {
						const fallback = savedViews.find((v) => v.id === s.fallbackViewId)
						const layout = clone(fallback ? fallback.layout : DEFAULT_LAYOUT)
						return {
							...layout,
							committed: clone(layout),
							savedViews,
							activeViewId: fallback ? fallback.id : null,
							defaultViewId,
							editMode: true
						}
					}
					return { savedViews, defaultViewId }
				})
				const { viewsAccountId, viewsAssetId } = get()
				if (viewsAccountId && viewsAssetId) {
					AnalyticsViews.remove(viewsAccountId, viewsAssetId, id).catch(
						() => undefined
					)
				}
			},
			setDefaultViewId: (id) => {
				set({ defaultViewId: id })
				const { viewsAccountId, viewsAssetId } = get()
				if (id && viewsAccountId && viewsAssetId) {
					AnalyticsViews.setDefault(viewsAccountId, viewsAssetId, id).catch(
						() => undefined
					)
				}
			},
			setViewScope: (id, scope) => {
				set((s) => {
					// Only the author may re-scope (make private/shared) a view.
					const v = s.savedViews.find((x) => x.id === id)
					if (!v || !isViewOwner(v)) return {}
					return {
						savedViews: s.savedViews.map((x) =>
							x.id === id ? { ...x, scope } : x
						)
					}
				})
				const { viewsAccountId, viewsAssetId } = get()
				if (viewsAccountId && viewsAssetId) {
					AnalyticsViews.update(viewsAccountId, viewsAssetId, id, {
						scope: scope === 'shared' ? 'shared' : 'private'
					}).catch(() => undefined)
				}
			},

			initMetricOrder: (ids) =>
				set((s) => {
					const metricOrder = reconcileOrder(s.metricOrder, ids)
					// Materializing the natural order isn't a user edit — seed it into the
					// committed snapshot too when that's still empty so it doesn't count as
					// an unsaved change. A real reorder later diverges only the draft.
					const committed = s.committed.metricOrder.length
						? s.committed
						: { ...s.committed, metricOrder: [...metricOrder] }
					return { metricOrder, committed }
				}),
			initChartOrder: (ids) =>
				set((s) => {
					const chartOrder = reconcileOrder(s.chartOrder, ids)
					const committed = s.committed.chartOrder.length
						? s.committed
						: { ...s.committed, chartOrder: [...chartOrder] }
					return { chartOrder, committed }
				}),
			reorderMetric: (from, to) =>
				set((s) => ({ metricOrder: arrayMove(s.metricOrder, from, to) })),
			reorderChart: (from, to) =>
				set((s) => ({ chartOrder: arrayMove(s.chartOrder, from, to) })),
			toggleMetricHidden: (id) =>
				set((s) => ({ hiddenMetrics: toggle(s.hiddenMetrics, id) })),
			toggleChartHidden: (id) =>
				set((s) => ({ hiddenCharts: toggle(s.hiddenCharts, id) })),
			setChartCfg: (id, patch) =>
				set((s) => {
					const prev = s.chartConfig[id] ?? DEFAULT_CHART_CONFIG[id]
					const next = { ...prev, ...patch }
					// Bar-only metrics (Bad traffic rate, Blocked sources) can't be a
					// donut/stacked bar, so switching the metric snaps an incompatible
					// chart type back to Bar (§4.6: "chart type automatically switches
					// to Bar", no error).
					next.chartType = compatibleChartType(next.metric, next.chartType)
					// The same dimension can never sit in both slots (DEV-2432 §5.5).
					// Picking for one slot the value the other holds SWAPS them — the
					// only reading that keeps both of the user's choices — and a
					// persisted clash falls back through resolveStackBy.
					if (next.dimension2 === next.dimension) {
						if (patch.dimension2 === undefined) next.dimension2 = prev.dimension
						else if (patch.dimension === undefined) next.dimension = prev.dimension2
						if (next.dimension2 === next.dimension)
							next.dimension2 = resolveStackBy(next.dimension, undefined)
					}
					return { chartConfig: { ...s.chartConfig, [id]: next } }
				}),
			// Metrics are UNIQUE across the tiles (Cherry #0002: five tiles all
			// reading "Blocked sources" is pointless). Enforced here, in the one
			// setter every entry point uses (on-card picker, customization drawer),
			// by swapping: if another tile — visible or hidden, so un-hiding can't
			// surface a duplicate — already shows the requested metric, it takes this
			// tile's current one. Nothing is ever locked in a picker.
			setMetricCfg: (id, patch) =>
				set((s) => {
					const cfgOf = (tid: string): MetricCfg =>
						s.metricConfig[tid] ?? DEFAULT_METRIC_CONFIG[tid]
					const next: Record<string, MetricCfg> = {
						...s.metricConfig,
						[id]: { ...cfgOf(id), ...patch }
					}
					if (patch.metric !== undefined && patch.metric !== cfgOf(id).metric) {
						const ids = s.metricOrder.length ? s.metricOrder : Object.keys(DEFAULT_METRIC_CONFIG)
						const holder = ids.find((tid) => tid !== id && cfgOf(tid).metric === patch.metric)
						if (holder) next[holder] = { ...cfgOf(holder), metric: cfgOf(id).metric }
					}
					return { metricConfig: next }
				}),
			setSectionName: (key, name) =>
				set((s) => ({ sectionNames: { ...s.sectionNames, [key]: name } })),
			resetChartSlot: (id) =>
				set((s) => ({
					chartConfig: {
						...s.chartConfig,
						[id]: {
							...(s.committed.chartConfig[id] ?? DEFAULT_CHART_CONFIG[id])
						}
					}
				})),
			resetMetricSlot: (id) =>
				set((s) => ({
					metricConfig: {
						...s.metricConfig,
						[id]: {
							...(s.committed.metricConfig[id] ?? DEFAULT_METRIC_CONFIG[id])
						}
					}
				})),

			setTableConfig: (cfg) =>
				set((s) =>
					// No-op if unchanged — the grid mirrors on every render, so guard
					// against redundant writes (which would otherwise churn `selectDirty`).
					sameTableConfig(s.tableConfig, cfg) ? {} : { tableConfig: cfg }
				)
		}),
		{
			name: 'protection-analytics-layout-v1',
			version: 6,
			storage: createJSONStorage(() => localStorage),
			// Persist ONLY the committed snapshot and the saved views — never the
			// transient draft or editMode. The draft (incl. the table settings, which
			// now live in the layout's `tableConfig`) is rehydrated from committed in
			// `merge`.
			partialize: (s) => ({
				committed: s.committed,
				savedViews: s.savedViews,
				activeViewId: s.activeViewId,
				defaultViewId: s.defaultViewId
			}),
			// On load, seed the working draft from the committed snapshot so the
			// report renders the last save and Discard has a target.
			merge: (persisted, current) => {
				const p = (persisted ?? {}) as Partial<ReportLayoutState>
				// Schedules used to be cached here; they now come from the backend per
				// view. Drop a stale persisted copy so it can't shadow the real record.
				delete (p as Record<string, unknown>).schedules
				const committed = p.committed
					? { ...DEFAULT_LAYOUT, ...p.committed }
					: current.committed
				// Defensive backfill: any persisted view missing `author` (pre-v6 data
				// that slipped past migrate) gets the unknown-author marker so the
				// owner check and author line never read `undefined` (and nobody is
				// wrongly treated as its owner).
				const savedViews = p.savedViews
					? p.savedViews.map((v) =>
							v.author ? v : { ...v, author: UNKNOWN_AUTHOR }
					  )
					: current.savedViews
				// Legacy branding was browser-wide and cannot be attributed to a website.
				delete (p as Record<string, unknown>).whiteLabelBranding
				return {
					...current,
					...p,
					savedViews,
					committed: clone(committed),
					...clone(committed)
				}
			},
			// v1–v3 used a different shape (activeView, flat layout fields, no draft/
			// commit). Drop them onto the factory layout (table settings now live in
			// the layout's tableConfig, so a clean DEFAULT_LAYOUT covers them).
			migrate: (persisted: any, version) => {
				if (version >= 6) return persisted
				// v4/v5 saved views predate `author`. Reseed the demo views so the new
				// owner/non-owner examples appear and every view has an author; keep the
				// user's own saves (id `v_*`) and attribute them to the current user.
				if (version >= 4) {
					const userSaves = Array.isArray(persisted?.savedViews)
						? persisted.savedViews
								.filter(
									(v: any) => typeof v?.id === 'string' && v.id.startsWith('v_')
								)
								.map((v: any) => ({ ...v, author: v.author ?? UNKNOWN_AUTHOR }))
						: []
					return {
						...persisted,
						savedViews: [
							...SEED_VIEWS.map((v) => ({ ...v, layout: clone(v.layout) })),
							...userSaves
						]
					}
				}
				return {
					committed: clone(DEFAULT_LAYOUT)
				}
			}
		}
	)
)
